import type { InitialEditorTarget } from '@canvas/model/ui';

import { element } from './util';

/**
 * Controls how a click on a rendered direct-edit slide target is exposed to the input
 * state machine.
 *
 * Direct-edit roots visually represent the whole slide child, so pointerdown uses
 * `ignore` to keep outer-entity selection and resize handles reachable. A later click
 * can use `text` to focus the same target directly once the outer child is selected.
 */
export type DirectEditRootSlideTargetPolicy = 'default' | 'ignore' | 'text';

export type GetInitialEditorTargetOptions = {
  directEditRootSlideTarget?: DirectEditRootSlideTargetPolicy;
};

/** Arguments passed to each resolver when trying to identify what was clicked. */
type InitialEditorTargetResolverArgs = GetInitialEditorTargetOptions & {
  targetElement: HTMLElement | SVGElement;
  clientX: number;
  clientY: number;
};

/** A function that checks whether a click hit a recognisable editor target. */
type InitialEditorTargetResolver = (
  args: InitialEditorTargetResolverArgs
) => InitialEditorTarget | undefined;

const slideTargetSelector =
  '[data-slide-node="slideTarget"][data-slide-id], [data-slide-node="slideTarget"][data-slide-target-id]';

/**
 * Identifies a clicked slide target, then reads the target ID from either the rendered
 * slide DOM (`data-slide-id`) or a mounted editor node view (`data-slide-target-id`).
 *
 * The rendered and mounted IDs share one path because both DOM shapes represent the
 * same logical slide target. The direct-edit-root policy only applies to rendered
 * slide output; mounted editor targets should always be reported to support switching
 * selected targets inside an active editor.
 */
const resolveSlideTargetInitialEditorTarget: InitialEditorTargetResolver = ({
  targetElement,
  clientX,
  clientY,
  directEditRootSlideTarget = 'default',
}) => {
  const editorTargetElement = targetElement.closest(slideTargetSelector);
  if (!editorTargetElement) {
    return undefined;
  }

  const renderedTargetId = editorTargetElement.getAttribute('data-slide-id');
  const mountedEditorTargetId = editorTargetElement.getAttribute('data-slide-target-id');
  const targetId = renderedTargetId || mountedEditorTargetId;

  if (!targetId) {
    return undefined;
  }

  let initialSelectionMode: 'text' | undefined;
  if (renderedTargetId && isRenderedDirectEditRootTarget(editorTargetElement)) {
    if (directEditRootSlideTarget === 'ignore') {
      return undefined;
    }

    initialSelectionMode = directEditRootSlideTarget === 'text' ? 'text' : undefined;
  }

  return {
    targetType: 'slideTarget',
    targetId,
    clientX,
    clientY,
    ...(initialSelectionMode ? { initialSelectionMode } : {}),
  };
};

/**
 * Returns true when the clicked rendered target belongs to a direct-edit slide root.
 *
 * The renderer writes `data-slide-direct-edit-root` from the slide capability registry,
 * which is the source of truth used by the mounted slides editor. Keeping this as an
 * attribute read prevents the input package from inferring slide behaviour by counting
 * DOM nodes.
 */
const isRenderedDirectEditRootTarget = (targetElement: Element): boolean =>
  targetElement
    .closest('[data-slide-node="slideElement"]')
    ?.getAttribute('data-slide-direct-edit-root') === 'true';

/**
 * Resolvers tried in order to identify which text box was clicked. Each checks the DOM
 * for a specific pattern (e.g. slide targets). To support new element types, add a
 * resolver here.
 */
const initialEditorTargetResolvers: InitialEditorTargetResolver[] = [
  resolveSlideTargetInitialEditorTarget,
];

/**
 * Checks whether the event target is inside a text box that the editor should activate
 * when it mounts. Returns the target info if found, `undefined` otherwise.
 *
 * Callers can override direct-edit-root target handling to model the click ladder:
 * pointerdown ignores direct-edit roots so the outer slide child can be selected, while
 * click can request text activation for that same target.
 */
export const getInitialEditorTarget = (
  target: EventTarget | null,
  clientX: number,
  clientY: number,
  options: GetInitialEditorTargetOptions = {}
): InitialEditorTarget | undefined => {
  const targetElement = element(target);
  if (!targetElement) {
    return undefined;
  }

  for (const resolver of initialEditorTargetResolvers) {
    const initialEditorTarget = resolver({
      targetElement,
      clientX,
      clientY,
      directEditRootSlideTarget: options.directEditRootSlideTarget,
    });
    if (initialEditorTarget) {
      return initialEditorTarget;
    }
  }

  return undefined;
};
